import { CheckOutlined, ShoppingCartOutlined, StarFilled } from '@ant-design/icons'
import { Badge, Button, Card, Col, Divider, InputNumber, message, Radio, Row, Space, Switch, Typography } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  AccountPublic, BillingPrice, BillingProduct, BillingSubscription, CountryChoice, Organization
} from '@pollination-solutions/pollination-sdk'

import { useAuth } from 'auth-context'
import useBillingAPI from 'hooks/useBillingAPI'
import { usePaddle } from 'hooks/usePaddleBilling'
import { InvoiceRequestFormData, InvoiceRequestModal } from '../InvoiceRequestModal'

const { Title, Text } = Typography

// Currency formatting helpers
const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  }
  return symbols[currencyCode] || currencyCode
}

const formatCurrency = (amount: string | number, currencyCode: string): string => {
  let numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  numAmount = numAmount / 100 // Convert cents to dollars
  const symbol = getCurrencySymbol(currencyCode)
  const formatted = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return `${symbol}${formatted}`
}

// Helper to determine if a price is monthly or yearly based on billing cycle
const getPricePeriod = (price: BillingPrice): 'monthly' | 'yearly' => {
  const interval = price.billing_cycle?.interval?.toLowerCase() || 'month'
  return interval.includes('year') ? 'yearly' : 'monthly'
}


// Group prices by billing period
const groupPricesByPeriod = (prices: BillingPrice[]) => {
  const monthly: BillingPrice[] = []
  const yearly: BillingPrice[] = []

  prices.forEach(price => {
    if (getPricePeriod(price) === 'monthly') {
      monthly.push(price)
    } else {
      yearly.push(price)
    }
  })

  return { monthly, yearly }
}

// Extract plan_multiplier from price data
const getPlanMultiplier = (price: BillingPrice): number => {
  return price.custom_data?.['plan_multiplier'] || 1
}

// Determine resource type based on product name
const getResourceType = (product: BillingProduct) => {
  const productName = product.name.toLowerCase()

  if (productName.includes('seat')) {
    return { type: 'seat', singular: 'organization seat', plural: 'organization seats' }
  } else if (productName.includes('rhino') && !productName.includes('bundled')) {
    return { type: 'rhino_license', singular: 'Rhino plugin license', plural: 'Rhino plugin licenses' }
  } else if (productName.includes('revit') && !productName.includes('bundled')) {
    return { type: 'revit_license', singular: 'Revit plugin license', plural: 'Revit plugin licenses' }
  } else if (productName.includes('bundled')) {
    return { type: 'bundled_license', singular: 'bundled plugin license', plural: 'bundled plugin licenses' }
  } else {
    return { type: 'resource', singular: 'resource', plural: 'resources' }
  }
}

// Calculate total resources with proper resource type aggregation
const calculateResourcesByType = (selectedProducts: SelectedProduct[]): { [key: string]: { count: number; type: ReturnType<typeof getResourceType> } } => {
  const resourcesByType: { [key: string]: { count: number; type: ReturnType<typeof getResourceType> } } = {}

  selectedProducts.forEach(item => {
    const resourceType = getResourceType(item.product)
    const planMultiplier = getPlanMultiplier(item.price)
    const totalForThisItem = planMultiplier * item.quantity

    // Handle bundled licenses specially
    if (resourceType.type === 'bundled_license') {
      // Split into individual Rhino and Revit licenses
      if (!resourcesByType['rhino_license']) {
        resourcesByType['rhino_license'] = {
          count: 0,
          type: { type: 'rhino_license', singular: 'Rhino plugin license', plural: 'Rhino plugin licenses' }
        }
      }
      resourcesByType['rhino_license'].count += totalForThisItem

      if (!resourcesByType['revit_license']) {
        resourcesByType['revit_license'] = {
          count: 0,
          type: { type: 'revit_license', singular: 'Revit plugin license', plural: 'Revit plugin licenses' }
        }
      }
      resourcesByType['revit_license'].count += totalForThisItem
    } else {
      if (!resourcesByType[resourceType.type]) {
        resourcesByType[resourceType.type] = { count: 0, type: resourceType }
      }
      resourcesByType[resourceType.type].count += totalForThisItem
    }
  })

  return resourcesByType
}


// Check if the selected billing period conflicts with existing subscriptions
const hasConflictingBillingPeriod = (
  selectedPeriod: 'monthly' | 'yearly',
  existingSubscriptions: BillingSubscription[]
): {
  hasConflict: boolean
  conflictingSubscription?: BillingSubscription
} => {
  const periodMap = {
    'monthly': 'month',
    'yearly': 'year'
  }
  // Find active subscriptions with conflicting billing periods
  const conflictingSubscription = existingSubscriptions.find(sub =>
    sub.status === 'active'
    && sub.billing_cycle?.interval === periodMap[selectedPeriod] // Only check active subscriptions
  )

  return {
    hasConflict: !!conflictingSubscription,
    conflictingSubscription
  }
}

// Check if selected products conflict with existing subscription products
const hasConflictingProducts = (
  selectedProducts: SelectedProduct[],
  existingSubscriptions: BillingSubscription[]
): {
  hasConflict: boolean
  conflictingProducts: string[]
} => {
  const conflictingProducts: string[] = []

  selectedProducts.forEach(selectedItem => {
    const productId = selectedItem.product.id
    const productName = selectedItem.product.name

    // Check if any active subscription already includes this product
    const isAlreadySubscribed = existingSubscriptions.some(subscription =>
      subscription.status === 'active' &&
      subscription.items?.some(item => item.product?.id === productId)
    )

    if (isAlreadySubscribed) {
      conflictingProducts.push(productName)
    }
  })

  return {
    hasConflict: conflictingProducts.length > 0,
    conflictingProducts
  }
}

interface Props {
  billingProducts: BillingProduct[]
  existingSubscriptions?: BillingSubscription[]
  account: AccountPublic
  onSuccess?: () => void
}

interface SelectedProduct {
  product: BillingProduct
  price: BillingPrice
  quantity: number
}

export const NewBillingSubscriptionForm: React.FC<Props> = ({ billingProducts, existingSubscriptions = [], account, onSuccess }) => {
  const { loading, sendInvoiceRequestConfirmation } = useBillingAPI()
  const { openCheckout, isInitializing, initError, isReady } = usePaddle()
  const { user } = useAuth()
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([])

  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly') // ✅Default to yearly to promote it
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'invoice'>('card') //✅
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)

  useEffect(() => {
    const billingPeriodConflict = hasConflictingBillingPeriod(billingPeriod, existingSubscriptions)
    if (billingPeriodConflict.hasConflict) {
      if (billingPeriod === 'yearly') {
        setBillingPeriod('monthly')
      } else {
        setBillingPeriod('yearly')
      }
    }
  }, [])

  // Reset payment method to card when switching to monthly billing ✅
  useEffect(() => {
    if (billingPeriod === 'monthly' && paymentMethod === 'invoice') {
      setPaymentMethod('card')
    }
  }, [billingPeriod, paymentMethod])

  // Analyze products and get available prices for the selected period
  const productAnalysis = useMemo(() => {
    return billingProducts.map(product => {
      const { monthly, yearly } = groupPricesByPeriod(product.prices || [])
      const prices = billingPeriod === 'monthly' ? monthly : yearly

      // Sort prices by unit price (ascending)
      const sortedPrices = [...prices].sort((a, b) => {
        const aPrice = parseFloat(a.unit_price?.amount || '0')
        const bPrice = parseFloat(b.unit_price?.amount || '0')
        return aPrice - bPrice
      })

      const isSeatProduct = product.plan_type === 'seat'
      const hasYearly = yearly.length > 0

      return {
        product,
        prices: sortedPrices,
        hasYearly,
        isBulkProduct: isSeatProduct && sortedPrices.length > 1,
        isSeatProduct
      }
    })
  }, [billingProducts, billingPeriod])

  // Calculate total estimate
  const totalEstimate = useMemo(() => {
    return selectedProducts.reduce((total, item) => {
      if (item.price.unit_price.amount) {
        return total + (parseFloat(item.price.unit_price.amount) * item.quantity)
      }
      return total
    }, 0)
  }, [selectedProducts])

  // Calculate validation results
  const validationResults = useMemo(() => {
    const billingPeriodConflict = hasConflictingBillingPeriod(billingPeriod, existingSubscriptions)
    const productConflicts = hasConflictingProducts(selectedProducts, existingSubscriptions)

    const res = {
      billingPeriodConflict,
      productConflicts,
      hasAnyConflict: billingPeriodConflict.hasConflict || productConflicts.hasConflict
    }

    return res
  }, [billingPeriod, selectedProducts, existingSubscriptions])

  const selectPrice = useCallback((product: BillingProduct, price: BillingPrice) => {
    setSelectedProducts(prev => {
      const existing = prev.find(item => item.price.id === price.id)
      if (existing) {
        // If already selected, increase quantity
        return prev.map(item =>
          item.price.id === price.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      } else {
        // Add new selection
        return [...prev, {
          product,
          price,
          quantity: 1
        }]
      }
    })
  }, [])

  const isSelected = useCallback((priceId: string) => {
    return selectedProducts.some(item => item.price.id === priceId)
  }, [selectedProducts])

  const getSelectedQuantity = useCallback((priceId: string) => {
    const item = selectedProducts.find(item => item.price.id === priceId)
    return item ? item.quantity : 0
  }, [selectedProducts])

  const updateQuantity = useCallback((priceId: string, newQuantity: number) => {
    setSelectedProducts(prev =>
      prev.map(item =>
        item.price.id === priceId
          ? { ...item, quantity: Math.max(1, newQuantity) }
          : item
      )
    )
  }, [])

  const removeSelection = useCallback((priceId: string) => {
    setSelectedProducts(prev => prev.filter(item => item.price.id !== priceId))
  }, [])

  const handleSubmit = useCallback(async () => {
    try {
      // Validate that all selected products have the same billing period
      const billingPeriods = selectedProducts.map(item => getPricePeriod(item.price))
      const uniquePeriods = new Set(billingPeriods)

      if (uniquePeriods.size > 1) {
        message.error('Cannot checkout with mixed billing periods. Please select only monthly or yearly prices.')
        return
      }

      // Check for subscription conflicts
      if (validationResults.billingPeriodConflict.hasConflict) {
        const conflictingSub = validationResults.billingPeriodConflict.conflictingSubscription
        message.error(`You already have a ${billingPeriod} subscription (ID: ${conflictingSub?.id?.substring(0, 8)}...). Please cancel it before creating a new ${billingPeriod} subscription.`)
        return
      }

      if (validationResults.productConflicts.hasConflict) {
        const conflictingProductsList = validationResults.productConflicts.conflictingProducts.join(', ')
        message.error(`You already have active subscriptions for: ${conflictingProductsList}. Please cancel existing subscriptions for these products before creating new ones.`)
        return
      }

      if (selectedProducts.length === 0) {
        console.error('No products selected')
        return
      }

      // Route to appropriate payment method
      if (paymentMethod === 'invoice') {
        // Open invoice request modal
        setShowInvoiceModal(true)
      } else {
        // Prepare checkout items in the format Paddle expects
        const checkoutItems = selectedProducts.map(item => ({
          priceId: item.price.id,
          quantity: item.quantity
        }))

        // Call the Paddle checkout
        openCheckout({
          url: window.location.href,
          items: checkoutItems,
          account,
          onSuccess: (data, err) => {
            if (err) {
              console.error('Paddle checkout error:', err)
              return
            }
            setSelectedProducts([])
            onSuccess?.()
          }
        })
      }
    } catch (error) {
      console.error('Failed to start checkout:', error)
    }
  }, [selectedProducts, account, openCheckout, onSuccess, billingPeriod, validationResults, paymentMethod])

  const handleInvoiceRequest = useCallback(async (formData: InvoiceRequestFormData, organization: Organization | null) => {
    try {
      // Prepare the request data with selected products in the format expected by the SDK
      const additionalEmails = formData.additional_contact_email
        ? [formData.additional_contact_email]
        : []

      const requestData = {
        account: account.name,
        address_line1: formData.billing_address,
        city: formData.city,
        region: formData.state,
        postal_code: formData.postal_code,
        country: formData.country as CountryChoice,
        items: selectedProducts.map(item => ({
          product_id: item.product.id,
          price_id: item.price.id,
          quantity: item.quantity
        })),
        additional_emails: additionalEmails
      }

      await sendInvoiceRequestConfirmation(requestData)

      // Clear selected products and close modal on success
      setSelectedProducts([])
      setShowInvoiceModal(false)
      onSuccess?.()
    } catch (error) {
      // Error is handled in the useBillingAPI hook
      console.error('Failed to submit invoice request:', error)
    }
  }, [selectedProducts, account, sendInvoiceRequestConfirmation, onSuccess])

  const canSubmit = selectedProducts.length > 0 && !loading &&
    (paymentMethod === 'invoice' || isReady) && !validationResults.hasAnyConflict

  return (
    <div>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={3}>Choose Your Pollination Products</Title>
          <Text type="secondary">
            Select the products and billing period that work best for your organization.
          </Text>

          {/* Billing Period Toggle */}
          <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
            <Text style={{ fontWeight: billingPeriod === 'monthly' ? 'bold' : 'normal' }}>Monthly</Text>
            <Switch
              checked={billingPeriod === 'yearly'}
              onChange={(checked) => {
                const newPeriod = checked ? 'yearly' : 'monthly'
                setBillingPeriod(newPeriod)
                // Clear selections when changing billing period to avoid conflicts
                if (selectedProducts.some(item => getPricePeriod(item.price) !== newPeriod)) {
                  setSelectedProducts(
                    selectedProducts.filter(item => getPricePeriod(item.price) === newPeriod).slice(0,
                      4
                    )
                  )
                }
              }}
              style={{
                backgroundColor: billingPeriod === 'yearly' ? '#52c41a' : undefined,
                transform: 'scale(1.2)'
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text style={{ fontWeight: billingPeriod === 'yearly' ? 'bold' : 'normal' }}>
                Yearly
              </Text>
              {billingPeriod === 'yearly' && (
                <Badge
                  count="Save 20%"
                  style={{
                    backgroundColor: '#52c41a',
                    fontSize: '10px',
                    height: '20px',
                    lineHeight: '20px',
                    animation: 'none'
                  }}
                />
              )}
            </div>
          </div>

          {/* Payment Method Selection - Only show for yearly billing */}
          {billingPeriod === 'yearly' && (
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
              <Radio.Group
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                buttonStyle="solid"
                size="large"
              >
                <Radio.Button value="card">Pay with Card</Radio.Button>
                <Radio.Button value="invoice">Pay by Invoice</Radio.Button>
              </Radio.Group>
            </div>
          )}
        </div>

        {/* Validation Warnings */}
        {validationResults.hasAnyConflict && (
          <div style={{ marginBottom: '24px' }}>
            {validationResults.billingPeriodConflict.hasConflict && (
              <div style={{
                padding: '12px',
                backgroundColor: '#fff2e8',
                border: '1px solid #ffbb96',
                borderRadius: '6px',
                marginBottom: '12px'
              }}>
                <Text type="warning" style={{ fontWeight: 'bold' }}>
                  ⚠️ Billing Period Conflict
                </Text>
                <div style={{ marginTop: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    You have an active subscription that conflicts with the selected billing period.
                    Please cancel it before creating a {billingPeriod} subscription.
                  </Text>
                </div>
              </div>
            )}

            {validationResults.productConflicts.hasConflict && (
              <div style={{
                padding: '12px',
                backgroundColor: '#fff2e8',
                border: '1px solid #ffbb96',
                borderRadius: '6px',
                marginBottom: '12px'
              }}>
                <Text type="warning" style={{ fontWeight: 'bold' }}>
                  ⚠️ Product Conflicts
                </Text>
                <div style={{ marginTop: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    You already have active subscriptions for: {validationResults.productConflicts.conflictingProducts.join(', ')}.
                    Please cancel existing subscriptions before selecting these products.
                  </Text>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Product Showcase */}
        <Row gutter={[24, 24]}>
          {productAnalysis.map(({ product, prices, hasYearly, isBulkProduct, isSeatProduct }) => (
            <Col xs={24} lg={isSeatProduct ? 24 : 8} xl={isSeatProduct ? 24 : 8} key={product.id}>
              <Card
                hoverable={prices.length > 0}
                style={{
                  height: '100%',
                  minHeight: '400px',
                  border: prices.some(price => isSelected(price.id)) ? '2px solid #1890ff' : '1px solid #e8e8e8',
                  backgroundColor: prices.some(price => isSelected(price.id)) ? '#f0f8ff' : '#ffffff',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column' }}
                cover={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '16px' }}>
                    <img src={product.image_url} alt={product.name} style={{ width: '60px', height: 'auto' }} />
                  </div>
                }
              >
                <div style={{ textAlign: 'center', marginBottom: '20px', flex: '0 0 auto' }}>
                  <Title level={4} style={{ margin: '0 0 8px 0', fontWeight: 'bold', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    {product.name}
                  </Title>
                  <Text type="secondary" style={{ display: 'block', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                    {product.description}
                  </Text>
                </div>

                {/* Pricing Options */}
                <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'flex-end' }}>
                  <Row gutter={[12, 12]} justify="center" align="stretch" style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
                    {prices.length === 0 ? (
                      <Col span={24}>
                        <Card
                          size="small"
                          style={{
                            backgroundColor: '#f5f5f5',
                            border: '1px dashed #d9d9d9',
                            textAlign: 'center'
                          }}
                          bodyStyle={{ padding: '24px' }}
                        >
                          <Text type="secondary">
                            No {billingPeriod} pricing available for this product
                          </Text>
                          <div style={{ marginTop: '8px' }}>
                            <Button
                              type="link"
                              size="small"
                              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                            >
                              View {billingPeriod === 'monthly' ? 'yearly' : 'monthly'} pricing
                            </Button>
                          </div>
                        </Card>
                      </Col>
                    ) : prices.map((price) => {
                      const selected = isSelected(price.id)
                      const quantity = getSelectedQuantity(price.id)
                      const unitPrice = parseFloat(price.unit_price.amount)

                      // Get resource count from plan_multiplier
                      const resourceCount = getPlanMultiplier(price)
                      // Calculate per-unit pricing for bundles (use resourceCount instead of parsing name)
                      const perUnitPrice = resourceCount > 1 ? unitPrice / resourceCount : unitPrice

                      return (
                        <Col
                          xs={24}
                          sm={12}
                          md={8}
                          lg={6}
                          xl={6}
                          key={price.id}
                          style={{ display: 'flex', minWidth: '200px' }}
                        >
                          <Card
                            key={price.id}
                            size="small"
                            style={{
                              backgroundColor: selected ? '#e6f7ff' : '#fafafa',
                              border: selected ? '2px solid #1890ff' : '1px solid #e8e8e8',
                              cursor: selected ? 'default' : 'pointer',
                              position: 'relative',
                              height: '100%',
                              width: '100%',
                              minWidth: '200px',
                              maxWidth: '280px',
                              flex: 1,
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                            onClick={selected ? undefined : () => selectPrice(product, price)}
                            bodyStyle={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: 1 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {selected && <CheckOutlined style={{ color: '#1890ff' }} />}
                                  <Text strong style={{ fontSize: '14px' }}>
                                    {price.name}
                                  </Text>
                                </div>

                                {price.description && (
                                  <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                                    {price.description}
                                  </Text>
                                )}

                                <div style={{ marginTop: '8px' }}>
                                  <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                                    {formatCurrency(unitPrice, price.unit_price.currency_code)}
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                      /{getPricePeriod(price) === 'yearly' ? 'year' : 'month'}
                                    </Text>
                                    {resourceCount > 1 && (
                                      <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal', display: 'block' }}>
                                        ({formatCurrency(perUnitPrice, price.unit_price.currency_code)} per {getResourceType(product).singular})
                                      </Text>
                                    )}
                                  </Title>

                                  {/* Show monthly equivalent for yearly pricing */}
                                  {getPricePeriod(price) === 'yearly' && (
                                    <div style={{ marginTop: '4px' }}>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>
                                        {formatCurrency(unitPrice / 12, price.unit_price.currency_code)}/month when paid yearly
                                      </Text>
                                    </div>
                                  )}

                                  {/* Resource count indicator */}
                                  {resourceCount > 0 && (
                                    <div style={{ marginTop: '4px' }}>
                                      {(() => {
                                        const resourceType = getResourceType(product)
                                        return (
                                          <Badge
                                            count={resourceType.type === 'bundled_license'
                                              ? `${resourceCount} Rhino + ${resourceCount} Revit licenses`
                                              : `${resourceCount} ${resourceCount === 1 ? resourceType.singular : resourceType.plural}`
                                            }
                                            style={{
                                              backgroundColor: '#1890ff',
                                              fontSize: '10px',
                                              height: '16px',
                                              lineHeight: '16px'
                                            }}
                                          />
                                        )
                                      })()}
                                    </div>
                                  )}
                                </div>
                                {/* Quantity selector for selected items */}
                                {selected && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      marginLeft: '12px',
                                      minWidth: '60px'
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    <div style={{ marginBottom: '4px' }}>
                                      <Text type="secondary" style={{ fontSize: '11px' }}>Qty:</Text>
                                    </div>
                                    <InputNumber
                                      key={`qty-${price.id}`}
                                      size="small"
                                      min={1}
                                      value={quantity}
                                      onChange={(value) => updateQuantity(price.id, value || 1)}
                                      style={{ width: '60px' }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Remove button for selected items */}
                            {selected && (
                              <Button
                                type="text"
                                danger
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  removeSelection(price.id)
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  width: '20px',
                                  height: '20px',
                                  padding: 0,
                                  fontSize: '12px'
                                }}
                              >
                                ×
                              </Button>
                            )}
                          </Card>
                        </Col>
                      )
                    })}
                  </Row>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Selected Products Summary */}
        {selectedProducts.length > 0 && (
          <div style={{ padding: '20px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
            <Title level={4} style={{ marginBottom: '16px' }}>
              Selected Products ({selectedProducts.length})
            </Title>

            <div style={{ marginBottom: '16px' }}>
              {selectedProducts.map((item, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div>
                    <Text strong>{item.product.name}</Text>
                    <Text type="secondary"> - {item.price.name} × {item.quantity}</Text>
                  </div>
                  <div>
                    <Text strong style={{ color: '#1890ff' }}>
                      {formatCurrency(parseFloat(item.price.unit_price.amount) * item.quantity, item.price.unit_price.currency_code)}
                      <Text type="secondary">/{getPricePeriod(item.price) === 'yearly' ? 'year' : 'month'}</Text>
                    </Text>
                  </div>
                </div>
              ))}
            </div>

            <Divider />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <Title level={4} style={{ margin: 0 }}>Total:</Title>
              <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
                {formatCurrency(totalEstimate, selectedProducts[0]?.price.unit_price.currency_code || 'USD')} + VAT/Tax
                <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                  /{billingPeriod === 'yearly' ? 'year' : 'month'}
                </Text>
              </Title>
            </div>
          </div>
        )}

        {/* Action Button */}
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <Button
            type="primary"
            size="large"
            loading={loading || isInitializing}
            disabled={!canSubmit}
            onClick={handleSubmit}
            icon={<ShoppingCartOutlined />}
            style={{
              height: '50px',
              fontSize: '16px',
              fontWeight: 'bold',
              paddingLeft: '32px',
              paddingRight: '32px'
            }}
          >
            {isInitializing
              ? 'Loading Checkout...'
              : paymentMethod === 'invoice'
                ? 'Request Invoice'
                : 'Proceed to Checkout'
            }
          </Button>

          {initError && (
            <div style={{ marginTop: '8px', color: '#ff4d4f' }}>
              <Text type="danger" style={{ fontSize: '12px' }}>
                ⚠️ Checkout unavailable: {initError}
              </Text>
            </div>
          )}

          {canSubmit && !initError && (
            <div style={{ marginTop: '8px' }}>
              {paymentMethod === 'invoice' ? (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  📧 You&apos;ll receive a confirmation email and staff will contact you within a few business days
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <StarFilled style={{ color: '#faad14' }} /> Secure checkout powered by Paddle
                </Text>
              )}
            </div>
          )}

          {validationResults.hasAnyConflict && (
            <div style={{ marginTop: '8px' }}>
              <Text type="danger" style={{ fontSize: '12px' }}>
                ⚠️ Please resolve conflicts above before proceeding
              </Text>
            </div>
          )}

          {selectedProducts.length === 0 && !initError && !validationResults.hasAnyConflict && (
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Please select at least one product to continue
              </Text>
            </div>
          )}
        </div>
      </Space>

      {/* Invoice Request Modal */}
      {user && (
        <InvoiceRequestModal
          visible={showInvoiceModal}
          user={user}
          account={account}
          onClose={() => setShowInvoiceModal(false)}
          onSubmit={handleInvoiceRequest}
          loading={loading}
          selectedProducts={selectedProducts}
          totalAmount={totalEstimate}
          currency={selectedProducts[0]?.price.unit_price.currency_code || 'USD'}
        />
      )}
    </div >
  )
}